// ============================================================================
//  Meetings Pages
// ============================================================================

import React, { useState, useRef } from 'react'
import { usersApi } from '@/modules/users/api/users.api'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CalendarDays,
  Plus,
  X,
  ChevronLeft,
  Users,
  MapPin,
  Clock,
  Play,
  CheckCircle,
  XCircle,
  Circle,
  AlertCircle,
  Edit2,
  CheckCircle2,
  MoreVertical,
  User,
  Save,
  Archive,
  Pin,
  Hash,
  Search,
  FileText,
  Edit,
  Check,
  Trash2,
  RotateCcw,
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Code,
  Tag,
  Folder,
} from 'lucide-react'
import {
  meetingsApi,
  type Meeting,
  type MeetingAction,
} from '@/modules/documents/api/documents.api'
import { useNotificationStore } from '@/stores/notification.store'

const fmtDateTime = (d: string) =>
  new Date(d).toLocaleString('fr-SN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short' }) : '—'

const MEETING_STATUS: Record<
  string,
  { label: string; color: string; bg: string; icon: React.ElementType }
> = {
  SCHEDULED: {
    label: 'Planifiée',
    color: 'text-[var(--blue-d)]',
    bg: 'bg-[var(--blue-bg)] border-[rgba(245,124,0,0.25)]',
    icon: Clock,
  },
  IN_PROGRESS: {
    label: 'En cours',
    color: 'text-[var(--amber-text)]',
    bg: 'bg-[var(--amber-bg)] border-[rgba(245,158,11,0.25)]',
    icon: Play,
  },
  COMPLETED: {
    label: 'Terminée',
    color: 'text-[var(--green-text)]',
    bg: 'bg-[var(--green)]/10 border-[rgba(16,185,129,0.25)]',
    icon: CheckCircle,
  },
  CANCELLED: {
    label: 'Annulée',
    color: 'text-[var(--red-text)]',
    bg: 'bg-[var(--red-bg)] border-[rgba(239,68,68,0.25)]',
    icon: XCircle,
  },
  POSTPONED: {
    label: 'Reportée',
    color: 'text-[var(--tx3)]',
    bg: 'bg-[var(--bd2)]/10 border-[var(--bd2)]/20',
    icon: AlertCircle,
  },
}

const ACTION_STATUS: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'À faire', color: 'text-[var(--amber-text)]' },
  IN_PROGRESS: { label: 'En cours', color: 'text-[var(--blue-d)]' },
  DONE: { label: 'Terminé', color: 'text-[var(--green-text)]' },
  CANCELLED: { label: 'Annulé', color: 'text-[var(--tx3)]' },
}

function Avatar({ user }: { user?: { firstName: string; lastName: string; avatarUrl?: string } }) {
  if (!user) return null
  const initials = `${user.firstName[0]}${user.lastName[0]}`
  return user.avatarUrl ? (
    <img src={user.avatarUrl} alt={initials} className="w-7 h-7 rounded-full object-cover" />
  ) : (
    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--tx)] text-xs font-bold shrink-0">
      {initials}
    </div>
  )
}

function Spinner() {
  return (
    <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
  )
}

export function MeetingsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data, isLoading } = useQuery({
    queryKey: ['meetings', { status, search }],
    queryFn: () => meetingsApi.list({ status: status || undefined, search: search || undefined }),
  })

  const meetings: Meeting[] = data?.data ?? data?.meetings ?? []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">Réunions</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            {meetings.length} réunion{meetings.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Planifier
        </button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="w-full px-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(MEETING_STATUS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : meetings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--tx3)]">
          <CalendarDays className="w-10 h-10 mb-3 opacity-20" />
          <p className="text-sm">Aucune réunion</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => {
            const cfg = MEETING_STATUS[m.status] ?? MEETING_STATUS.SCHEDULED
            const StatusIcon = cfg.icon
            return (
              <div
                key={m.id}
                onClick={() => navigate(`/meetings/${m.id}`)}
                className="bg-[var(--surface)] border border-[var(--bd)] rounded-xl p-4 hover:border-[var(--blue)] hover:shadow-sm transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon className="w-3 h-3" />{cfg.label}
                      </span>
                      <span className="text-xs text-[var(--tx3)]">{m.type}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-[var(--tx)] mb-1">{m.title}</h3>
                    <div className="flex items-center gap-4 text-xs text-[var(--tx3)]">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(m.startDate ?? m.startTime)}</span>
                      {m.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</span>}
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{m.participants.length} participant{m.participants.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <div className="flex -space-x-2">
                      {m.participants.slice(0, 4).map((p) => (
                        <Avatar key={p.id} user={p.user} />
                      ))}
                      {m.participants.length > 4 && (
                        <div className="w-7 h-7 rounded-full bg-[var(--surface-2)] border border-[var(--bd)] flex items-center justify-center text-[10px] text-[var(--tx3)]">
                          +{m.participants.length - 4}
                        </div>
                      )}
                    </div>
                    <button onClick={e => { e.stopPropagation(); navigate(`/meetings/${m.id}`) }} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
                      <ChevronLeft className="w-4 h-4 text-[var(--tx3)] rotate-180" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showCreate && (
        <CreateMeetingModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['meetings'] })
            addNotification({ type: 'success', title: 'Réunion planifiée', message: '' })
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}

function CreateMeetingModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const now = new Date()
  const [form, setForm] = useState({
    title: '',
    type: 'REGULAR',
    startDate: now.toISOString().slice(0, 16),
    endDate: new Date(now.getTime() + 60 * 60 * 1000).toISOString().slice(0, 16),
    location: '',
    meetingUrl: '',
    description: '',
    participantIds: [] as string[],
  })
  const [participantSearch, setParticipantSearch] = useState('')
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }))

  const { mutate, isPending } = useMutation({ mutationFn: () => meetingsApi.create(form), onSuccess: onCreated })
  const TYPES = ['REGULAR', 'WEEKLY', 'MONTHLY', 'BOARD', 'PROJECT', 'ONE_ON_ONE', 'OTHER']
  const TYPE_LABELS: Record<string, string> = { REGULAR: 'Régulière', WEEKLY: 'Hebdomadaire', MONTHLY: 'Mensuelle', BOARD: 'Comité', PROJECT: 'Projet', ONE_ON_ONE: 'One-on-One', OTHER: 'Autre' }

  // Recherche utilisateurs pour participants
  const { data: usersData } = useQuery({
    queryKey: ['users-search-create-meeting', participantSearch],
    queryFn: () => usersApi.directory({ search: participantSearch, limit: 8 }),
    enabled: participantSearch.length >= 2,
  })
  const searchResults: any[] = (usersData ?? []).filter((u: any) => !form.participantIds.includes(u.id))

  // Stocker les infos des participants sélectionnés pour l'affichage
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])

  const addParticipant = (user: any) => {
    set('participantIds', [...form.participantIds, user.id])
    setSelectedUsers((prev) => [...prev, user])
    setParticipantSearch('')
  }

  const removeParticipant = (userId: string) => {
    set('participantIds', form.participantIds.filter((id: string) => id !== userId))
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId))
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Planifier une réunion</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors"><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Titre */}
          <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Titre de la réunion *" className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />

          {/* Type */}
          <select value={form.type} onChange={(e) => set('type', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]">
            {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>)}
          </select>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Début *</label>
              <input type="datetime-local" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-[var(--tx3)] mb-1">Fin *</label>
              <input type="datetime-local" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none" />
            </div>
          </div>

          {/* Lieu */}
          <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Lieu / salle (optionnel)" className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />

          {/* Lien visio */}
          <input value={form.meetingUrl} onChange={(e) => set('meetingUrl', e.target.value)} placeholder="Lien visio — Google Meet, Zoom… (optionnel)" className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />

          {/* Description */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Description (optionnel)</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Ordre du jour, objectifs…" rows={3} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none" />
          </div>

          {/* Participants */}
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-1">Participants</label>
            {/* Chips des participants sélectionnés */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedUsers.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--surface-2)] border border-[var(--bd)] rounded-full text-xs text-[var(--tx)]">
                    <span className="w-5 h-5 rounded-full bg-[var(--blue)] text-white flex items-center justify-center text-[10px] font-bold">
                      {(u.firstName?.[0] ?? '').toUpperCase()}{(u.lastName?.[0] ?? '').toUpperCase()}
                    </span>
                    {u.firstName} {u.lastName}
                    <button onClick={() => removeParticipant(u.id)} className="text-[var(--tx3)] hover:text-red-500 ml-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {/* Recherche */}
            <div className="relative">
              <input
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                placeholder="Rechercher un collaborateur…"
                className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
              />
              {participantSearch.length >= 2 && searchResults.length > 0 && (
                <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border border-[var(--bd)] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((u: any) => (
                    <button
                      key={u.id}
                      onClick={() => addParticipant(u)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--surface-2)] text-left text-sm transition-colors"
                    >
                      <span className="w-7 h-7 rounded-full bg-[var(--blue)] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.firstName?.[0] ?? '').toUpperCase()}{(u.lastName?.[0] ?? '').toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <div className="text-[var(--tx)] font-medium truncate">{u.firstName} {u.lastName}</div>
                        <div className="text-[var(--tx3)] text-xs truncate">{u.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {participantSearch.length >= 2 && searchResults.length === 0 && (
                <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border border-[var(--bd)] rounded-lg shadow-lg p-3 text-xs text-[var(--tx3)] text-center">
                  Aucun résultat
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button onClick={() => mutate()} disabled={isPending || !form.title} className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Planifier
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
//  Notes Page — éditeur Tiptap + redesign NexusCorp
// ============================================================================

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { notesApi, type Note } from '@/modules/documents/api/documents.api'
import { useAuthStore } from '@/stores/auth.store'

const NOTE_COLORS = ['', '#fef3c7', '#fce7f3', '#dbeafe', '#dcfce7', '#f3e8ff', '#fee2e2']

export function NotesPage() {
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)
  const [search, setSearch] = useState('')
  const [myNotes, setMyNotes] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)
  const { user } = useAuthStore()

  const { data, isLoading } = useQuery({
    queryKey: ['notes', { search, myNotes, showArchived }],
    queryFn: () => showArchived
      ? (notesApi as any).getArchived ? (notesApi as any).getArchived() : notesApi.list({ search: search || undefined })
      : notesApi.list({ search: search || undefined, myNotes: myNotes || undefined }),
  })

  const restoreMutation = useMutation({
    mutationFn: (id: string) => (notesApi as any).restore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  })

  const notes: Note[] = data?.data ?? data?.notes ?? (Array.isArray(data) ? data : [])
  const pinned = notes.filter((n) => n.isPinned && !n.isArchived)
  const recent = notes.filter((n) => !n.isPinned && !n.isArchived)

  const openNote = async (note: Note) => {
    const full = await notesApi.get(note.id)
    setSelectedNote(full)
  }

  const archiveMutation = useMutation({
    mutationFn: (id: string) => notesApi.archive(id),
    onSuccess: (_: any, id: string) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      if (selectedNote?.id === id) setSelectedNote(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notesApi.delete(id),
    onSuccess: (_: any, id: string) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      if (selectedNote?.id === id) setSelectedNote(null)
    },
  })

  const pinMutation = useMutation({
    mutationFn: (id: string) => notesApi.pin(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  })

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden rounded-xl" style={{ border: '1px solid var(--bd)' }}>

      {/* ── Sidebar sombre NexusCorp ─────────────────────────────────────── */}
      <div className="w-72 shrink-0 flex flex-col" style={{ background: 'var(--sb-bg)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>

        {/* Header sidebar */}
        <div className="px-4 pt-4 pb-3 space-y-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: 'var(--brand-orange)' }}>
                <FileText className="w-3 h-3" style={{ color: '#FFFFFF' }} />
              </div>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em' }}>Notes</h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowArchived(v => !v)}
                title={showArchived ? 'Voir les notes actives' : 'Archives'}
                className="p-1.5 rounded-lg transition-all"
                style={{ color: showArchived ? '#F59E0B' : 'var(--tx3)', background: showArchived ? 'rgba(245,158,11,0.12)' : 'transparent' }}
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              {!showArchived && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="p-1.5 rounded-lg transition-all"
                  style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--tx3)' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-full pl-8 pr-3 py-1.5 text-xs focus:outline-none rounded-lg"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--bd)' }}
            />
          </div>

          {/* Toggle mes notes */}
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setMyNotes(v => !v)}
              className="w-7 h-4 rounded-full transition-colors relative cursor-pointer shrink-0"
              style={{ background: myNotes ? 'var(--brand-orange)' : 'rgba(255,255,255,0.12)' }}
            >
              <div className="w-3 h-3 rounded-full absolute top-0.5 transition-all" style={{ background: '#FFFFFF', left: myNotes ? 14 : 2 }} />
            </div>
            <span style={{ fontSize: 11, color: 'var(--tx3)' }}>Mes notes uniquement</span>
          </label>
        </div>

        {/* Liste notes */}
        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--brand-orange)', borderTopColor: 'transparent' }} />
            </div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <FileText className="w-8 h-8 opacity-20" style={{ color: 'var(--tx3)' }} />
              <p style={{ fontSize: 12, color: 'var(--tx3)' }}>{showArchived ? 'Aucune note archivée' : 'Aucune note'}</p>
            </div>
          ) : showArchived ? (
            <div>
              <p className="px-2 pb-1.5" style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Archives ({notes.length})
              </p>
              {notes.map((n) => (
                <div key={n.id} className="group flex items-center gap-1">
                  <NoteItem note={n} isActive={selectedNote?.id === n.id} onClick={() => openNote(n)} />
                  <button
                    onClick={() => restoreMutation.mutate(n.id)}
                    title="Restaurer"
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded transition-all shrink-0 mr-1"
                    style={{ color: '#10B981' }}
                  >
                    <RotateCcw className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <>
              {pinned.length > 0 && (
                <div className="mb-2">
                  <p className="px-2 pb-1.5" style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    📌 Épinglées
                  </p>
                  {pinned.map((n) => (
                    <NoteItem key={n.id} note={n} isActive={selectedNote?.id === n.id} onClick={() => openNote(n)} />
                  ))}
                </div>
              )}
              {recent.length > 0 && (
                <div>
                  <p className="px-2 pb-1.5" style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Récentes
                  </p>
                  {recent.map((n) => (
                    <NoteItem key={n.id} note={n} isActive={selectedNote?.id === n.id} onClick={() => openNote(n)} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Zone éditeur ──────────────────────────────────────────────────── */}
      {selectedNote ? (
        <NoteEditor
          key={selectedNote.id}
          note={selectedNote}
          onPin={() => {
            pinMutation.mutate(selectedNote.id)
            queryClient.invalidateQueries({ queryKey: ['notes'] })
          }}
          onArchive={() => archiveMutation.mutate(selectedNote.id)}
          onDelete={() => {
            if (window.confirm('Supprimer définitivement cette note ?'))
              deleteMutation.mutate(selectedNote.id)
          }}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center" style={{ background: '#FAFAFA' }}>
          <div className="flex flex-col items-center gap-4 text-center max-w-xs">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--bd)' }}>
              <FileText className="w-7 h-7" style={{ color: 'var(--brand-amber)' }} />
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--brand-dark)', marginBottom: 6 }}>Aucune note sélectionnée</p>
              <p style={{ fontSize: 13, color: 'var(--tx3)' }}>Choisissez une note dans la liste ou créez-en une nouvelle</p>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all"
              style={{ background: 'var(--brand-orange)', color: '#FFFFFF', fontSize: 13, fontWeight: 600 }}
            >
              <Plus className="w-4 h-4" /> Nouvelle note
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateNoteModal
          onClose={() => setShowCreate(false)}
          onCreated={async (note) => {
            queryClient.invalidateQueries({ queryKey: ['notes'] })
            setShowCreate(false)
            openNote(note)
          }}
        />
      )}
    </div>
  )
}

// ── NoteItem ──────────────────────────────────────────────────────────────────
function NoteItem({ note, isActive, onClick }: { note: Note; isActive: boolean; onClick: () => void }) {
  const fmtAge = (d: string) => {
    const h = (Date.now() - new Date(d).getTime()) / 3600000
    if (h < 1) return "À l'instant"
    if (h < 24) return `${Math.floor(h)}h`
    return `${Math.floor(h / 24)}j`
  }
  const accentColor = note.color || 'var(--brand-orange)'
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-2.5 rounded-lg transition-all"
      style={{
        background: isActive ? 'rgba(245,124,0,0.14)' : 'transparent',
        borderLeft: `2.5px solid ${isActive ? accentColor : 'transparent'}`,
        paddingLeft: isActive ? 10 : 12,
      }}
      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.05)' }}
      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
    >
      <div className="flex items-center justify-between mb-0.5">
        <p className="truncate flex-1" style={{ fontSize: 12, fontWeight: isActive ? 600 : 500, color: isActive ? '#FFFFFF' : 'var(--tx3)' }}>
          {note.title}
        </p>
        {note.isPinned && <span style={{ fontSize: 10, marginLeft: 4 }}>📌</span>}
      </div>
      <p className="line-clamp-1" style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 1 }}>
        {note.plainText || 'Note vide'}
      </p>
      <p style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>{fmtAge(note.updatedAt)}</p>
    </button>
  )
}

// ── NoteEditor ────────────────────────────────────────────────────────────────
function NoteEditor({
  note,
  onPin,
  onArchive,
  onDelete,
}: {
  note: Note
  onPin: () => void
  onArchive: () => void
  onDelete?: () => void
}) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(note.title)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Placeholder.configure({ placeholder: 'Commencez à écrire votre note…' }),
      ],
      content: note.content,
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none focus:outline-none min-h-full px-12 py-6',
        },
      },
      onUpdate: ({ editor }) => {
        clearTimeout(autoSaveTimeoutRef.current)
        setSaving(false)
        autoSaveTimeoutRef.current = setTimeout(async () => {
          setSaving(true)
          try {
            await notesApi.autoSave(note.id, {
              content: editor.getJSON(),
              plainText: editor.getText(),
            })
            setLastSaved(new Date())
            queryClient.invalidateQueries({ queryKey: ['notes'] })
          } finally {
            setSaving(false)
          }
        }, 2000)
      },
    },
    [note.id],
  )

  const saveTitleMutation = useMutation({
    mutationFn: () => notesApi.update(note.id, { title }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  })

  // Toolbar button helper
  const ToolBtn = ({ icon: Icon, action, active, title: t }: { icon: any; action: () => void; active?: boolean; title: string }) => (
    <button
      onClick={action}
      title={t}
      className="p-1.5 rounded-lg transition-all"
      style={{
        color: active ? 'var(--sb-bg)' : 'var(--tx3)',
        background: active ? 'var(--brand-orange)' : 'transparent',
      }}
      onMouseEnter={e => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)'
          ;(e.currentTarget as HTMLElement).style.color = 'var(--tx2)'
        }
      }}
      onMouseLeave={e => {
        if (!active) {
          ;(e.currentTarget as HTMLElement).style.background = 'transparent'
          ;(e.currentTarget as HTMLElement).style.color = 'var(--tx3)'
        }
      }}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  )

  return (
    <div className="flex-1 flex flex-col min-w-0" style={{ background: '#FFFFFF' }}>

      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-4 shrink-0"
        style={{ background: '#FAFAFA', borderBottom: '1px solid var(--bd)', minHeight: 48 }}
      >
        <div className="flex items-center gap-0.5">
          <ToolBtn icon={Bold}          action={() => editor?.chain().focus().toggleBold().run()}                      active={editor?.isActive('bold')}                    title="Gras" />
          <ToolBtn icon={Italic}        action={() => editor?.chain().focus().toggleItalic().run()}                    active={editor?.isActive('italic')}                  title="Italique" />
          <ToolBtn icon={Strikethrough} action={() => editor?.chain().focus().toggleStrike().run()}                    active={editor?.isActive('strike')}                  title="Barré" />
          <div className="w-px h-4 mx-1.5" style={{ background: 'var(--bd)' }} />
          <ToolBtn icon={Heading1}      action={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}       active={editor?.isActive('heading', { level: 1 })}   title="Titre 1" />
          <ToolBtn icon={Heading2}      action={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}       active={editor?.isActive('heading', { level: 2 })}   title="Titre 2" />
          <div className="w-px h-4 mx-1.5" style={{ background: 'var(--bd)' }} />
          <ToolBtn icon={List}          action={() => editor?.chain().focus().toggleBulletList().run()}                active={editor?.isActive('bulletList')}              title="Liste à puces" />
          <ToolBtn icon={ListOrdered}   action={() => editor?.chain().focus().toggleOrderedList().run()}               active={editor?.isActive('orderedList')}             title="Liste numérotée" />
          <ToolBtn icon={Code}          action={() => editor?.chain().focus().toggleCodeBlock().run()}                 active={editor?.isActive('codeBlock')}               title="Bloc de code" />
        </div>

        {/* Actions droite */}
        <div className="flex items-center gap-1.5">
          {saving ? (
            <span style={{ fontSize: 11, color: 'var(--tx4)' }} className="animate-pulse mr-2">Sauvegarde…</span>
          ) : lastSaved ? (
            <span style={{ fontSize: 11, color: 'var(--tx4)' }} className="mr-2">
              Sauvegardé {lastSaved.toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : null}
          <div className="w-px h-4 mr-1" style={{ background: 'var(--bd)' }} />
          <button
            onClick={onPin}
            title={note.isPinned ? 'Désépingler' : 'Épingler'}
            className="p-1.5 rounded-lg transition-all"
            style={{ color: note.isPinned ? '#F59E0B' : 'var(--tx3)' }}
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onArchive}
            title="Archiver"
            className="p-1.5 rounded-lg transition-all"
            style={{ color: 'var(--tx3)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#F59E0B' }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--tx3)' }}
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
          {onDelete && (
            <button
              onClick={() => onDelete?.()}
              title="Supprimer définitivement"
              className="p-1.5 rounded-lg transition-all"
              style={{ color: 'var(--tx3)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#EF4444'; (e.currentTarget as HTMLElement).style.background = '#FEF2F2' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--tx3)'; (e.currentTarget as HTMLElement).style.background = 'transparent' }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Titre + métadonnées */}
      <div className="px-12 pt-10 pb-4 shrink-0" style={{ borderBottom: '1px solid var(--surface-2)' }}>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== note.title && saveTitleMutation.mutate()}
          className="w-full bg-transparent focus:outline-none"
          placeholder="Titre de la note…"
          style={{ fontSize: 28, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.03em', lineHeight: 1.2 }}
        />
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <span style={{ fontSize: 12, color: 'var(--tx4)' }}>
            Par {note.author?.firstName ?? ''} {note.author?.lastName ?? ''}
          </span>
          {note.lastEditedBy && (
            <span style={{ fontSize: 12, color: 'var(--tx4)' }}>
              · Modifié par {note.lastEditedBy?.firstName ?? ''} {note.lastEditedBy?.lastName ?? ''}
            </span>
          )}
          {note.folder && (
            <span className="flex items-center gap-1" style={{ fontSize: 12, color: 'var(--tx3)' }}>
              <Folder className="w-3 h-3" /> {note.folder.name}
            </span>
          )}
          {note.tags?.length > 0 && note.tags.map((t) => (
            <span key={t} className="flex items-center gap-1" style={{ fontSize: 11, color: 'var(--brand-amber)', background: 'var(--blue-bg)', padding: '2px 8px', borderRadius: 12 }}>
              <Tag className="w-2.5 h-2.5" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Éditeur Tiptap */}
      <div className="flex-1 overflow-y-auto" style={{ background: '#FFFFFF' }}>
        <EditorContent editor={editor} className="min-h-full" />
      </div>
    </div>
  )
}

// ── CreateNoteModal ───────────────────────────────────────────────────────────
function CreateNoteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (note: Note) => void }) {
  const [title, setTitle] = useState('')
  const [color, setColor] = useState('')
  const { mutate, isPending } = useMutation({
    mutationFn: () => notesApi.create({ title, color: color || undefined }),
    onSuccess: onCreated,
  })

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Nouvelle note</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre de la note *"
            autoFocus
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
          <div>
            <label className="block text-xs text-[var(--tx3)] mb-2">Couleur</label>
            <div className="flex gap-2">
              {NOTE_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-[var(--bd2)] scale-110' : 'border-transparent hover:scale-105'}`}
                  style={{ background: c || '#334155' }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button
            onClick={() => mutate()}
            disabled={isPending || !title}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {isPending ? <Spinner /> : <Plus className="w-4 h-4" />} Créer
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── MeetingDetailPage ────────────────────────────────────────────────────────

function RsvpBanner({ meeting, currentUserId, onRsvp, isPending }: {
  meeting: any; currentUserId?: string; onRsvp: (status: 'ACCEPTED' | 'DECLINED') => void; isPending: boolean
}) {
  const myParticipation = meeting?.participants?.find(
    (p: any) => p.userId === currentUserId || p.user?.id === currentUserId
  )
  if (!myParticipation || !['PENDING', 'INVITED'].includes(myParticipation.status)) return null
  return (
    <div className="mt-3 pt-3 border-t border-[var(--bd)]">
      <p className="text-xs text-[var(--tx3)] mb-2 text-center">Vous êtes invité — confirmez votre présence</p>
      <div className="flex gap-2">
        <button onClick={() => onRsvp('ACCEPTED')} disabled={isPending} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[var(--green-bg)] hover:bg-[#A7F3D0] text-[#065F46] rounded-lg text-xs font-medium transition-colors disabled:opacity-50">
          <CheckCircle className="w-3.5 h-3.5" /> Accepter
        </button>
        <button onClick={() => onRsvp('DECLINED')} disabled={isPending} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#FEE2E2] hover:bg-[#FECACA] text-[var(--red-text)] rounded-lg text-xs font-medium transition-colors disabled:opacity-50">
          <XCircle className="w-3.5 h-3.5" /> Décliner
        </button>
      </div>
    </div>
  )
}

export function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const { user: currentUser } = useAuthStore()
  const [notes, setNotes] = useState('')
  const [showAddParticipant, setShowAddParticipant] = useState(false)
  const [participantSearch, setParticipantSearch] = useState('')
  const [newAction, setNewAction] = useState({ title: '', dueDate: '', assigneeId: '' })
  const [showAddAction, setShowAddAction] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  const { data: meeting, isLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id!),
    enabled: !!id,
  })

  const startMutation = useMutation({ mutationFn: () => meetingsApi.start(id!), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); addNotification({ type: 'success', title: 'Réunion démarrée', message: '' }) } })
  const completeMutation = useMutation({ mutationFn: () => meetingsApi.complete(id!, notes || undefined), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); addNotification({ type: 'success', title: 'Réunion terminée', message: '' }) } })
  const cancelMutation = useMutation({ mutationFn: () => meetingsApi.cancel(id!), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); addNotification({ type: 'success', title: 'Réunion annulée', message: '' }) } })
  const saveNotesMutation = useMutation({ mutationFn: () => meetingsApi.updateNotes(id!, notes), onSuccess: () => addNotification({ type: 'success', title: 'Notes sauvegardées', message: '' }) })
  const addParticipantMutation = useMutation({ mutationFn: (userId: string) => meetingsApi.addParticipant(id!, userId), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); setShowAddParticipant(false); setParticipantSearch('') } })

  const { data: usersData } = useQuery({
    queryKey: ['users-search-meeting', participantSearch],
    queryFn: () => usersApi.directory({ search: participantSearch, limit: 8 }),
    enabled: participantSearch.length >= 2,
  })
  const existingUserIds = meeting?.participants?.map((p: any) => p.userId) ?? []
  const searchResults: any[] = (usersData ?? []).filter((u: any) => !existingUserIds.includes(u.id))

  const addActionMutation = useMutation({
    mutationFn: () => meetingsApi.addAction(id!, { title: newAction.title, assigneeId: newAction.assigneeId, dueDate: new Date(newAction.dueDate).toISOString() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); setNewAction({ title: '', assigneeId: '', dueDate: '' }); setShowAddAction(false) },
  })
  const rsvpMutation = useMutation({ mutationFn: (status: 'ACCEPTED' | 'DECLINED') => meetingsApi.rsvp(id!, status), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); addNotification({ type: 'success', title: 'Réponse envoyée', message: '' }) } })
  const removeParticipantMutation = useMutation({ mutationFn: (participantId: string) => (meetingsApi as any).removeParticipant(id!, participantId), onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting', id] }) })
  const toggleActionMutation = useMutation({ mutationFn: ({ actionId, done }: { actionId: string; done: boolean }) => meetingsApi.updateAction(id!, actionId, { status: done ? 'DONE' : 'PENDING' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting', id] }) })
  const deleteActionMutation = useMutation({ mutationFn: (actionId: string) => (meetingsApi as any).deleteAction(id!, actionId), onSuccess: () => qc.invalidateQueries({ queryKey: ['meeting', id] }) })
  const updateMeetingMutation = useMutation({ mutationFn: (data: any) => meetingsApi.update(id!, data), onSuccess: () => { qc.invalidateQueries({ queryKey: ['meeting', id] }); qc.invalidateQueries({ queryKey: ['meetings'] }); setShowEditModal(false) } })

  if (isLoading) return <div className="p-6 flex justify-center"><div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" /></div>
  if (!meeting) return <div className="p-6 text-[var(--tx3)]">Réunion introuvable.</div>

  const status = MEETING_STATUS[meeting.status] ?? MEETING_STATUS.SCHEDULED
  const StatusIcon = status.icon

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-start gap-4">
        <button onClick={() => navigate('/meetings')} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors mt-0.5">
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${status.bg} ${status.color}`}>
              <StatusIcon className="w-3 h-3" /> {status.label}
            </span>
            <span className="text-xs text-[var(--tx3)] uppercase tracking-wide">{meeting.type}</span>
          </div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">{meeting.title}</h1>
          {meeting.description && <p className="text-sm text-[var(--tx3)] mt-1">{meeting.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status) && (
            <button onClick={() => setShowEditModal(true)} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors text-[var(--tx3)] hover:text-[var(--tx)]" title="Modifier">
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {meeting.status === 'SCHEDULED' && (
            <button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
              <Play className="w-4 h-4" /> Démarrer
            </button>
          )}
          {meeting.status === 'IN_PROGRESS' && (
            <button onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending} className="flex items-center gap-2 px-4 py-2 bg-[var(--green)] hover:bg-[#059669] text-white rounded-lg text-sm font-medium disabled:opacity-50">
              <CheckCircle className="w-4 h-4" /> Terminer
            </button>
          )}
          {['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status) && (
            <button onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending} className="flex items-center gap-2 px-4 py-2 border border-[var(--bd)] hover:bg-[var(--red-bg)] text-[var(--red-text)] rounded-lg text-sm disabled:opacity-50">
              <XCircle className="w-4 h-4" /> Annuler
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: CalendarDays, label: 'Début', value: fmtDateTime(meeting.startDate ?? meeting.startTime ?? '') },
          { icon: Clock, label: 'Fin', value: fmtDateTime(meeting.endDate ?? meeting.endTime ?? '') },
          { icon: MapPin, label: 'Lieu', value: meeting.location ?? '—' },
          { icon: Users, label: 'Lien visio', value: meeting.meetingUrl ? meeting.meetingUrl.replace(/^https?:\/\//, '').slice(0, 30) + '…' : '—', link: meeting.meetingUrl },
        ].map(({ icon: Icon, label, value, link }: any) => (
          <div key={label} className="nx-card p-4">
            <div className="flex items-center gap-2 text-xs text-[var(--tx3)] mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
            {link ? (
              <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[var(--blue)] hover:underline truncate block">{value}</a>
            ) : (
              <p className="text-sm font-medium text-[var(--tx)]">{value}</p>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Participants */}
        <div className="nx-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[var(--tx)] flex items-center gap-2"><Users className="w-4 h-4 text-[var(--tx3)]" /> Participants ({meeting.participants?.length ?? 0})</h2>
            {['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status) && (
              <button onClick={() => setShowAddParticipant(true)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
                <Plus className="w-4 h-4 text-[var(--tx3)]" />
              </button>
            )}
          </div>
          <div className="space-y-2">
            {meeting.participants?.map((p: any) => (
              <div key={p.id} className="group flex items-center gap-3 py-1.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-white text-xs font-bold">
                  {p.user?.firstName?.[0] ?? '?'}{p.user?.lastName?.[0] ?? ''}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--tx)] truncate">{p.user?.firstName ?? ''} {p.user?.lastName ?? ''}</p>
                  <p className="text-xs text-[var(--tx3)]">{p.user?.email ?? ''}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.status === 'ACCEPTED' ? 'bg-[var(--green-bg)] text-[#065F46]' : p.status === 'DECLINED' ? 'bg-[#FEE2E2] text-[var(--red-text)]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                  {p.status === 'ACCEPTED' ? 'Accepté' : p.status === 'DECLINED' ? 'Décliné' : p.status === 'INVITED' ? 'Invité' : 'En attente'}
                </span>
                {['SCHEDULED', 'IN_PROGRESS'].includes(meeting.status) && (
                  <button onClick={() => window.confirm(`Retirer ${p.user?.firstName ?? ''} ?`) && removeParticipantMutation.mutate(p.id)} className="p-1 hover:bg-red-50 rounded text-[var(--tx4)] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 ml-1" title="Retirer">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {(!meeting.participants || meeting.participants.length === 0) && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Aucun participant</p>
            )}
          </div>
          <RsvpBanner meeting={meeting} currentUserId={currentUser?.id} onRsvp={(status) => rsvpMutation.mutate(status)} isPending={rsvpMutation.isPending} />
          {showAddParticipant && (
            <div className="mt-3 pt-3 border-t border-[var(--bd)] space-y-2">
              <div className="flex gap-2">
                <input value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} placeholder="Rechercher un utilisateur…" autoFocus className="flex-1 px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
                <button onClick={() => { setShowAddParticipant(false); setParticipantSearch('') }} className="px-3 py-2 border border-[var(--bd)] rounded-lg text-xs text-[var(--tx3)] hover:bg-[var(--surface-2)]"><X className="w-4 h-4" /></button>
              </div>
              {participantSearch.length >= 2 && (
                <div className="bg-white border border-[var(--bd)] rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <p className="text-xs text-[var(--tx3)] text-center py-3">Aucun utilisateur trouvé</p>
                  ) : (
                    searchResults.map((u: any) => (
                      <button key={u.id} onClick={() => addParticipantMutation.mutate(u.id)} disabled={addParticipantMutation.isPending} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--surface-2)] text-left transition-colors disabled:opacity-50">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">{u.firstName?.[0]}{u.lastName?.[0]}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--tx)] truncate">{u.firstName} {u.lastName}</p>
                          <p className="text-xs text-[var(--tx3)] truncate">{u.email}</p>
                        </div>
                        <Plus className="w-4 h-4 text-[var(--tx3)] shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              )}
              {participantSearch.length < 2 && <p className="text-xs text-[var(--tx3)] text-center py-1">Tapez au moins 2 caractères…</p>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="nx-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[var(--tx)] flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--tx3)]" /> Actions ({meeting.actions?.length ?? 0})</h2>
            <button onClick={() => setShowAddAction(true)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
              <Plus className="w-4 h-4 text-[var(--tx3)]" />
            </button>
          </div>
          <div className="space-y-2">
            {meeting.actions?.map((a: any) => {
              const isDone = a.status === 'DONE'
              return (
                <div key={a.id} className="group flex items-start gap-2 py-1.5 rounded-lg px-1 hover:bg-[var(--surface-2)] transition-colors">
                  <button onClick={() => toggleActionMutation.mutate({ actionId: a.id, done: !isDone })} className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${isDone ? 'bg-[var(--green)] border-[var(--green)]' : 'border-[var(--tx4)] hover:border-[var(--blue)]'}`}>
                    {isDone && <Check className="w-2.5 h-2.5 text-white" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${isDone ? 'line-through text-[var(--tx3)]' : 'text-[var(--tx)]'}`}>{a.title ?? a.description}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {a.assignee && <span className="text-[10px] text-[var(--tx3)]">→ {a.assignee?.firstName}</span>}
                      {a.dueDate && <span className={`text-[10px] ${new Date(a.dueDate) < new Date() && !isDone ? 'text-red-500' : 'text-[var(--tx3)]'}`}>{fmtDate(a.dueDate)}</span>}
                    </div>
                  </div>
                  <button onClick={() => window.confirm('Supprimer cette action ?') && deleteActionMutation.mutate(a.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded text-[var(--tx4)] hover:text-red-500 transition-all shrink-0" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )
            })}
            {(!meeting.actions || meeting.actions.length === 0) && <p className="text-xs text-[var(--tx3)] text-center py-4">Aucune action</p>}
          </div>
          {showAddAction && (
            <div className="mt-3 pt-3 border-t border-[var(--bd)] space-y-2">
              <input value={newAction.title} onChange={e => setNewAction(p => ({ ...p, title: e.target.value }))} placeholder="Titre de l'action… (obligatoire)" className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
              <select value={newAction.assigneeId} onChange={e => setNewAction(p => ({ ...p, assigneeId: e.target.value }))} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[var(--blue)]">
                <option value="">Responsable (obligatoire)…</option>
                {meeting.participants?.map((p: any) => (
                  <option key={p.userId} value={p.userId}>{p.user?.firstName ?? ''} {p.user?.lastName ?? ''}</option>
                ))}
              </select>
              <input type="date" value={newAction.dueDate} onChange={e => setNewAction(p => ({ ...p, dueDate: e.target.value }))} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
              <div className="flex gap-2">
                <button onClick={() => addActionMutation.mutate()} disabled={!newAction.title || !newAction.assigneeId || !newAction.dueDate || addActionMutation.isPending} className="flex-1 py-1.5 bg-[var(--blue)] text-white rounded-lg text-xs font-medium disabled:opacity-50">Ajouter</button>
                <button onClick={() => setShowAddAction(false)} className="px-3 py-1.5 border border-[var(--bd)] rounded-lg text-xs text-[var(--tx3)]">Annuler</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="nx-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[var(--tx)] flex items-center gap-2"><Edit2 className="w-4 h-4 text-[var(--tx3)]" /> Notes de réunion</h2>
          <button onClick={() => saveNotesMutation.mutate()} disabled={saveNotesMutation.isPending} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-xs font-medium disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> Sauvegarder
          </button>
        </div>
        <textarea value={notes || meeting.notes || ''} onChange={e => setNotes(e.target.value)} placeholder="Saisissez les notes de la réunion…" rows={6} className="w-full px-4 py-3 bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl text-sm text-[var(--tx)] placeholder:text-[var(--tx4)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none" />
      </div>

      {showEditModal && (
        <EditMeetingModal meeting={meeting} onClose={() => setShowEditModal(false)} onSave={(data: any) => updateMeetingMutation.mutate(data)} isPending={updateMeetingMutation.isPending} />
      )}
    </div>
  )
}

function EditMeetingModal({ meeting, onClose, onSave, isPending }: { meeting: any; onClose: () => void; onSave: (data: any) => void; isPending: boolean }) {
  const [form, setForm] = React.useState({
    title: meeting.title ?? '',
    description: meeting.description ?? '',
    location: meeting.location ?? '',
    meetingUrl: meeting.meetingUrl ?? '',
    startDate: meeting.startDate ? meeting.startDate.slice(0, 16) : '',
    endDate: meeting.endDate ? meeting.endDate.slice(0, 16) : '',
  })
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Modifier la réunion</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg"><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Titre *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          </div>
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none" />
          </div>
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Lieu</label>
            <input value={form.location} onChange={e => set('location', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          </div>
          <div>
            <label className="text-xs text-[var(--tx3)] mb-1 block">Lien visio</label>
            <input value={form.meetingUrl} onChange={e => set('meetingUrl', e.target.value)} placeholder="https://meet.google.com/…" className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--tx3)] mb-1 block">Début</label>
              <input type="datetime-local" value={form.startDate} onChange={e => set('startDate', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-[var(--tx3)] mb-1 block">Fin</label>
              <input type="datetime-local" value={form.endDate} onChange={e => set('endDate', e.target.value)} className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">Annuler</button>
          <button onClick={() => form.title.trim() && onSave({ title: form.title.trim(), description: form.description.trim() || undefined, location: form.location.trim() || undefined, meetingUrl: form.meetingUrl.trim() || undefined, startDate: form.startDate || undefined, endDate: form.endDate || undefined })} disabled={!form.title.trim() || isPending} className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {isPending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── NoteDetailPage ───────────────────────────────────────────────────────────
export function NoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data: note, isLoading } = useQuery({
    queryKey: ['note', id],
    queryFn: () => notesApi.get(id!),
    enabled: !!id,
  })

  const archiveMutation = useMutation({ mutationFn: () => notesApi.archive(id!), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['notes'] }); navigate(-1) } })
  const deleteMutation = useMutation({ mutationFn: () => notesApi.delete(id!), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['notes'] }); navigate(-1) } })
  const pinMutation = useMutation({ mutationFn: () => notesApi.pin(id!), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['note', id] }) })

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
    </div>
  )
  if (!note) return (
    <div className="p-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[var(--tx3)] hover:text-[var(--tx)] mb-6">
        <ChevronLeft className="w-4 h-4" /> Retour
      </button>
      <p className="text-[var(--tx3)]">Note introuvable.</p>
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 py-3 border-b border-[var(--bd)] shrink-0">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium text-[var(--tx)] truncate flex-1">{note.title}</span>
        {note.isPinned && <span className="text-xs text-[var(--amber-text)]">📌 Épinglée</span>}
      </div>
      <div className="flex-1 overflow-hidden">
        <NoteEditor
          key={note.id}
          note={note}
          onPin={() => pinMutation.mutate()}
          onArchive={() => { if (window.confirm('Archiver cette note ?')) archiveMutation.mutate() }}
          onDelete={() => { if (window.confirm('Supprimer définitivement cette note ?')) deleteMutation.mutate() }}
        />
      </div>
    </div>
  )
}
